import type { ModalStateProps } from '@proton/components/components/modalTwo/useModalState'
import Checkbox from '@proton/components/components/input/Checkbox'
import ModalTwo from '@proton/components/components/modalTwo/Modal'
import ModalTwoContent from '@proton/components/components/modalTwo/ModalContent'
import SelectTwo from '@proton/components/components/selectTwo/SelectTwo'
import Option from '@proton/components/components/option/Option'
import { useModalTwoStatic } from '@proton/components/components/modalTwo/useModalTwo'
import { Button } from '@proton/atoms/Button/Button'
import { IcInfoCircle } from '@proton/icons/icons/IcInfoCircle'
import { IcTrash } from '@proton/icons/icons/IcTrash'
import { SheetImportDestination, SheetImportSeparatorType, type SheetImportData } from '@proton/docs-shared'
import { SupportedProtonDocsMimeTypes } from '@proton/shared/lib/drive/constants'
import { useFlag } from '@proton/unleash/useFlag'
import clsx from '@proton/utils/clsx'
import { useEffect, useMemo, useRef, useState } from 'react'
import { c } from 'ttag'

export interface SheetImportModalProps extends ModalStateProps {
  handleImport: (data: SheetImportData) => void
}

const MIMETYPES_THAT_CAN_ONLY_REPLACE_SPREADSHEET: string[] = [
  SupportedProtonDocsMimeTypes.xlsx,
  SupportedProtonDocsMimeTypes.ods,
]

const CSV_ONLY_DESTINATIONS = [
  SheetImportDestination.InsertAsNewSheet,
  SheetImportDestination.ReplaceAtSelectedCell,
  SheetImportDestination.ReplaceCurrentSheet,
]

function SheetImportModal({ handleImport, onClose, open, ...modalProps }: SheetImportModalProps) {
  const [isOpen, setIsOpen] = useState(open)

  const isODSImportEnabled = useFlag('SheetsODSImportEnabled')
  const MIMETYPES_TO_ACCEPT = useMemo(() => {
    const mimeTypes = [
      SupportedProtonDocsMimeTypes.csv,
      SupportedProtonDocsMimeTypes.tsv,
      SupportedProtonDocsMimeTypes.xlsx,
    ]
    if (isODSImportEnabled) {
      mimeTypes.push(SupportedProtonDocsMimeTypes.ods)
    }
    return mimeTypes.join(', ')
  }, [isODSImportEnabled])

  const [file, setFile] = useState<File>()
  const [shouldConvertCells, setShouldConvertCells] = useState(true)
  const [destination, setDestination] = useState<SheetImportDestination>(SheetImportDestination.InsertAsNewSheet)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!file?.type) {
      return
    }
    if (
      MIMETYPES_THAT_CAN_ONLY_REPLACE_SPREADSHEET.includes(file.type) &&
      destination !== SheetImportDestination.ReplaceSpreadsheet
    ) {
      setDestination(SheetImportDestination.ReplaceSpreadsheet)
    }
    if (
      !MIMETYPES_THAT_CAN_ONLY_REPLACE_SPREADSHEET.includes(file.type) &&
      !CSV_ONLY_DESTINATIONS.includes(destination)
    ) {
      setDestination(SheetImportDestination.InsertAsNewSheet)
    }
  }, [destination, file?.type])

  const closeModal = () => {
    setIsOpen(false)
    if (typeof onClose !== 'undefined') {
      onClose()
    }
  }

  const canOnlyReplaceSpreadsheet = MIMETYPES_THAT_CAN_ONLY_REPLACE_SPREADSHEET.includes(file?.type ?? '')

  return (
    <ModalTwo
      data-testid="sheet-import-modal"
      fullscreen={false}
      size="medium"
      open={isOpen === undefined ? true : isOpen}
      onClose={closeModal}
      {...modalProps}
    >
      <ModalTwoContent className="mx-0 py-4">
        <div className="px-8">
          <h1 className="mb-1.5 text-2xl font-bold">{c('sheets_2025:Title').t`Import data`}</h1>
          <div className="mb-2 text-[--text-hint]">{c('sheets_2025:Info').t`Select a file to import`}</div>
          <div className={clsx('flex items-center', file ? 'mb-2' : 'mb-4')}>
            <span className="mr-2 text-[--text-hint]">{c('Label').t`File`}</span>
            {file ? (
              <>
                <span className="mr-1 max-w-[20ch] overflow-hidden text-ellipsis" title={file.name}>
                  {file.name}
                </span>
                <Button icon shape="ghost" onClick={() => setFile(undefined)}>
                  <IcTrash className="text-[--signal-danger]" />
                  <span className="sr-only">{c('Action').t`Remove file`}</span>
                </Button>
              </>
            ) : (
              <>
                <label className="sr-only">
                  {c('Label').t`File`}
                  <input
                    ref={inputRef}
                    type="file"
                    accept={MIMETYPES_TO_ACCEPT}
                    onChange={(event) => {
                      if (!event.target.files || event.target.files.length !== 1) {
                        return
                      }
                      const file = event.target.files[0]
                      if (!MIMETYPES_TO_ACCEPT.includes(file.type)) {
                        return
                      }
                      setFile(file)
                      setShouldConvertCells(true)
                    }}
                  />
                </label>
                <Button size="small" onClick={() => inputRef.current?.click()}>
                  Browse
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="border-weak border-y px-8 pb-5 pt-4">
          <div className="grid grid-cols-2 grid-rows-[auto,1fr] gap-x-4 gap-y-2">
            <div className="text-[--text-hint] [grid-column:1]">{c('sheets_2025:Label').t`Destination`}</div>
            <SelectTwo value={destination} onValue={setDestination} className="[grid-column:1]">
              <Option
                title={c('sheets_2025:Info').t`Insert as new sheet`}
                value={SheetImportDestination.InsertAsNewSheet}
                disabled={canOnlyReplaceSpreadsheet}
              />
              <Option
                title={c('sheets_2025:Info').t`Replace spreadsheet`}
                value={SheetImportDestination.ReplaceSpreadsheet}
                disabled={canOnlyReplaceSpreadsheet}
              />
              <Option
                title={c('sheets_2025:Info').t`Replace data at selected cell`}
                value={SheetImportDestination.ReplaceAtSelectedCell}
                disabled={canOnlyReplaceSpreadsheet}
              />
              <Option
                title={c('sheets_2025:Info').t`Replace current sheet`}
                value={SheetImportDestination.ReplaceCurrentSheet}
                disabled={canOnlyReplaceSpreadsheet}
              />
            </SelectTwo>
            <div className="text-[--text-hint] [grid-column:2] [grid-row:1]">{c('sheets_2025:Label')
              .t`Separator type`}</div>
            <div className="self-center [grid-column:2]">{c('sheets_2025:Info').t`Detect automatically`}</div>
          </div>
          <div className="h-4" />
          <div className="flex flex-col gap-2">
            <div className="text-[--text-hint]">{c('sheets_2025:Label').t`Format`}</div>
            <Checkbox
              checked={shouldConvertCells}
              onChange={(event) => {
                const checked = event.target.checked
                setShouldConvertCells(checked)
              }}
              disabled={canOnlyReplaceSpreadsheet}
            >{c('sheets_2025:Label').t`Convert text to numbers, dates, and formulas`}</Checkbox>
          </div>
        </div>
        {file?.type === SupportedProtonDocsMimeTypes.ods && (
          <div className="mt-2 flex items-center gap-2 px-8 text-sm text-[--text-hint]">
            <IcInfoCircle />
            <span>{c('Info').t`Importing ODS files is currently in beta`}</span>
          </div>
        )}
        {destination === SheetImportDestination.ReplaceSpreadsheet && (
          <div className="mt-2 flex items-center gap-2 bg-[--signal-warning-minor-2] px-8 py-2 text-sm">
            <IcInfoCircle className="text-[--signal-warning]" />
            <span>{c('Warning').t`This will replace all the data in the spreadsheet`}</span>
          </div>
        )}
        <div className="flex gap-4 px-8 pt-4">
          <Button className="ml-auto" onClick={closeModal}>{c('Action').t`Cancel`}</Button>
          <Button
            disabled={!file}
            shape="solid"
            color="norm"
            onClick={() => {
              if (!file) {
                return
              }
              handleImport({
                file,
                shouldConvertCellContents: shouldConvertCells,
                destination,
                separatorType: SheetImportSeparatorType.DetectAutomatically,
              })
              closeModal()
            }}
          >{c('Action').t`Import data`}</Button>
        </div>
      </ModalTwoContent>
    </ModalTwo>
  )
}

export function useSheetImportModal() {
  return useModalTwoStatic(SheetImportModal)
}
